import { z } from 'zod';
import { apiGet, apiPost, apiSend } from '@/lib/api';

/**
 * ⚠️ `vmCode` (`VendingMachines.VMCODE`), nie `code` — kolumny `CODE` w SPS nie ma.
 * Nazwą automatu jest `DESCR`, więc pole `name` mapuje na nią.
 *
 * `modeTypes` to TABLICA, nie jedna liczba: tryb adresowania należy do BĘBNA
 * (`PICAddressMode` ma klucz `(VMID, PICNUMBER)`), a automat z dostawką może mieć bębny
 * w różnych trybach — 9 = bęben natywny, 25 = DynaBox.
 */
export const MachineRowSchema = z.object({
  id: z.string(),
  vmid: z.number(),
  name: z.string(),
  vmCode: z.string().nullable(),
  modeTypes: z.array(z.number()),
  drumCount: z.number(),
  /** co RAPORTUJE sam automat (kolumna SPS) — nie to samo co „portal go słyszy" */
  online: z.boolean(),
  lastSeenAt: z.string().nullable(),
  /**
   * Stan agenta WYLICZONY po stronie API. Cztery wartości, bo cztery różne sytuacje:
   * `NO_AGENT` (nikt nie zainstalował — zadanie wdrożeniowe), `NEVER_CONNECTED` (instalacja
   * nie doszła do końca), `SILENT` (awaria), `ONLINE`. Wcześniej wszystkie cztery mieściły
   * się w jednym boolu `online` i portal mówił „offline" o maszynie, której nigdy nie pytał.
   */
  presence: z.enum(['NO_AGENT', 'NEVER_CONNECTED', 'ONLINE', 'SILENT']),
  minutesSinceContact: z.number().nullable(),
  /** kontakt AGENTA — to jest „ostatni kontakt”; `lastSeenAt` wyżej to samo-raport automatu */
  agentLastSeenAt: z.string().nullable(),
  /**
   * CZY OPROGRAMOWANIE AUTOMATU CHODZI.
   *
   * ⚠️ `null` = AGENT TEGO NIE SPRAWDZA (starsza wersja albo maszyna nie-Windows) i nie wolno go
   * czytać jako „nie działa". ⚠️ To także NIE to samo co `presence`: agent potrafi meldować się
   * co minutę na maszynie, na której SPSUI padło godzinę temu — i taka maszyna wyglądała dotąd
   * w portalu na całkowicie zdrową.
   */
  spsProcessRunning: z.boolean().nullable().optional(),
  /** Pod jaką nazwą agent szukał procesu — bez tego „nie działa" nie mówi, czego szukano. */
  spsProcess: z.string().nullable().optional(),
  spsProcessAt: z.string().nullable().optional(),
  /**
   * CZY CHODZI NARZĘDZIE NAPRAWCZE SPS-a (`SPSMaint.exe`).
   *
   * ⚠️ TO NIE JEST DRUGI ODCIEŃ „SPSUI NIE DZIAŁA". Ten proces startuje wtedy, gdy SPS-owi
   * rozsypuje się połączenie z bazą — czyli opisuje awarię bazy widzianą od strony automatu.
   * Dlatego na ekranie jest CZERWONY, a sam brak interfejsu tylko żółty: interfejs bywa
   * wyłączany celowo, a Maint nie startuje sam z siebie.
   */
  spsMaintRunning: z.boolean().nullable().optional(),
  spsMaint: z.string().nullable().optional(),
  spsMaintAt: z.string().nullable().optional(),
  /**
   * Kiedy Maint był widziany OSTATNI RAZ — ślad po przebiegu krótszym niż odstęp pomiarów.
   *
   * ⚠️ BEZ TEGO CAŁA KOLUMNA ŁAPAŁABY AWARIĘ PRZEZ PRZYPADEK: agent pyta systemu raz na minutę,
   * a naprawa bazy potrafi przelecieć w kilkanaście sekund.
   */
  spsMaintSeenAt: z.string().nullable().optional(),
  /**
   * CZY BAZA WYSPY ODPOWIADA — mierzy WYŁĄCZNIE agent, który tę bazę ma.
   *
   * ⚠️ `null` NA MASZYNIE PODLEGŁEJ JEST POPRAWNY, a nie brakujący: ona bazy nie dotyka
   * z założenia. Dlatego stan bazy pokazujemy tylko przy plakietce „BAZA WYSPY".
   */
  spsDbOk: z.boolean().nullable().optional(),
  spsDbAt: z.string().nullable().optional(),
  spsDbError: z.string().nullable().optional(),
  contradiction: z.boolean(),
  agent: z
    .object({
      nodeId: z.string(),
      version: z.string().nullable(),
      enrolledAt: z.string(),
      /** Nazwa komputera z Windowsa — bez niej nie wiadomo, która maszyna jest która. */
      hostname: z.string().nullable().optional(),
    })
    .nullable(),
  /**
   * Agent tej maszyny czeka na potwierdzenie, więc portal nie przyjmuje od niego danych.
   *
   * ⚠️ OSOBNE POLE OD `presence`, nie piąta jego wartość. Taki agent melduje się co minutę, więc
   * po obecności jest `ONLINE` — i to prawda. „Żyje" i „wolno mu pisać" to dwa różne pytania,
   * a jedno pole nie może odpowiadać na oba.
   */
  agentAwaitingAcceptance: z.boolean(),
  /**
   * NA TYM AUTOMACIE STOI BAZA WYSPY.
   *
   * ⚠️ To informacja operacyjna, nie ciekawostka: tej maszyny nie wolno wyłączyć ani przenieść
   * bez zatrzymania synchronizacji CAŁEJ wyspy. U DBW baza stoi na automacie 2 i z ekranu nie
   * dało się tego odczytać w żaden sposób.
   *
   * `false` przy wszystkich automatach jest poprawne — baza może stać na osobnym serwerze SQL,
   * który nie jest automatem.
   */
  hasDatabase: z.boolean().optional(),
  databaseName: z.string().nullable().optional(),
  compartments: z.number(),
  /** Wszystkie przegródki automatu — mianownik dla `compartments`. `optional`: starsza instancja go nie odda. */
  compartmentsTotal: z.number().optional(),
  fillPercent: z.number().nullable(),
  pending: z.boolean(),
});

export type MachineRow = z.infer<typeof MachineRowSchema>;

const MachinesPageSchema = z.object({
  rows: z.array(MachineRowSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export type ListMachinesParams = {
  groupId: string;
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
  onlyOnline: boolean;
};

export const listMachines = (p: ListMachinesParams) => {
  const q = new URLSearchParams({
    groupId: p.groupId,
    page: String(p.page),
    pageSize: String(p.pageSize),
    sort: p.sort,
    dir: p.dir,
    onlyOnline: String(p.onlyOnline),
  });
  if (p.search) q.set('search', p.search);
  return apiGet(`/machines?${q.toString()}`, MachinesPageSchema);
};

/** `VMID` to JEDYNY realnie liczbowy klucz naturalny w SPS, więc tylko tu podpowiadamy numer. */
export const fetchNextVmid = (groupId: string) =>
  apiGet(`/machines/next-vmid?groupId=${groupId}`, z.object({ nextVmid: z.number() }));

export type CreateMachinePayload = {
  groupId: string;
  vmid?: number;
  name: string;
  vmCode?: string;
  /**
   * Ile bębnów ma automat. KAŻDY dostaje własny wiersz `PICAddressMode`, bo bez trybu
   * adresowania bęben nie startuje.
   */
  drumCount: number;
  /** 9 = bęben natywny/BiPoint, 25 = DynaBox. */
  modeType?: 9 | 25;
};

export const createMachine = (payload: CreateMachinePayload) =>
  apiSend('POST', '/machines', payload, z.object({ opId: z.string(), id: z.string(), vmid: z.number() }));

/** `drumCount` NIE jest edytowalny: dorzucenie bębna to nowy wiersz `PICAddressMode`. */
export type UpdateMachinePayload = { name?: string; vmCode?: string | null };

export const updateMachine = (id: string, payload: UpdateMachinePayload) =>
  apiSend('PATCH', `/machines/${id}`, payload, z.object({ opId: z.string() }));

/**
 * `SPSConfig.XML` MASZYNY — baza, serwer i numer automatu.
 *
 * ⚠️ `zgloszone` TO OSTATNI MELDUNEK AGENTA, nie odczyt pliku na żywo. Portal nie ma połączenia
 * z maszyną poza kanałem agenta, więc wartość mogła się na niej zmienić po ostatnim rozpoznaniu —
 * ekran musi to powiedzieć, zamiast udawać podgląd na żywo.
 */
const StanSpsConfigSchema = z.object({
  machineId: z.string(),
  zgloszone: z.object({
    baza: z.string().nullable(),
    serwer: z.string().nullable(),
    vmid: z.number().nullable(),
    kiedy: z.string().nullable(),
  }),
  vmidWPortalu: z.number(),
  agent: z.object({ jest: z.boolean(), potwierdzony: z.boolean(), online: z.boolean() }),
  /** Identyfikator zadania, jeśli jedno już czeka — drugiego portal nie przyjmie. */
  zadanieWToku: z.string().nullable(),
  /** Treść ostrzeżenia o historii wydań; ekran ma je pokazać DOSŁOWNIE, nie streszczać. */
  ostrzezenieVmid: z.string(),
});

export type StanSpsConfig = z.infer<typeof StanSpsConfigSchema>;

export const fetchSpsConfig = (machineId: string, groupId: string) =>
  apiGet(`/machines/${machineId}/spsconfig?groupId=${groupId}`, StanSpsConfigSchema);

export type ZapiszSpsConfigPayload = {
  groupId: string;
  baza?: string;
  serwer?: string;
  vmid?: number;
  /** Bez tego API odmawia zmiany numeru — patrz ostrzeżenie o historii wydań. */
  rozumiemSkutkiVmid?: boolean;
};

export const zapiszSpsConfig = (machineId: string, payload: ZapiszSpsConfigPayload) =>
  apiSend(
    'POST',
    `/machines/${machineId}/spsconfig`,
    payload,
    z.object({ id: z.string(), zmiany: z.array(z.string()), ostrzezenia: z.array(z.string()) }),
  );

/**
 * URUCHOMIENIE INTERFEJSU AUTOMATU (`SPSUI.exe`) na maszynie.
 *
 * ⚠️ `dolaczone` znaczy „takie zlecenie już było w toku i dostajesz JEGO numer" — nie „zrobiono
 * drugie". Klikanie, gdy na ekranie automatu nic się nie dzieje, jest odruchem, a każde zadanie
 * to osobne uruchomienie programu na maszynie klienta.
 */
export const uruchomSpsui = (machineId: string, groupId: string) =>
  apiSend(
    'POST',
    `/machines/${machineId}/spsui?groupId=${groupId}`,
    {},
    z.object({ taskId: z.string(), dolaczone: z.boolean() }),
  );

/**
 * SKRÓT URUCHOMIENIOWY NA MASZYNIE — z paska w oknie pulpitu.
 *
 * ⚠️⚠️ WYSYŁAMY NAZWĘ ZE ZBIORU, NIGDY ŚCIEŻKI. Portal nie mówi agentowi, CO wykonać, tylko
 * KTÓRY ze znanych mu skrótów uruchomić — inaczej ten przycisk byłby zdalnym uruchamianiem
 * czegokolwiek na automacie wydającym towar.
 */
export type SkrotMaszyny = 'SPS_UTILITIES' | 'SPS_RAPORTY' | 'KATALOG_PRZESLANE' | 'MENEDZER_ZADAN' | 'URUCHOM';

export const uruchomSkrot = (machineId: string, groupId: string, co: SkrotMaszyny) =>
  apiPost(
    `/machines/${machineId}/skrot?groupId=${encodeURIComponent(groupId)}`,
    { groupId, co },
    z.object({ taskId: z.string(), dolaczone: z.boolean() }),
  );

/**
 * RESTART ALBO WYLACZENIE MASZYNY.
 *
 * ⚠️⚠️ `potwierdzone: true` JEST W CIELE ZADANIA, a nie tylko w ekranie. Uzytkownik zazadal
 * "walidacji klikniecia na obu" — i to musi stac takze po stronie serwera, bo ekran mozna ominac,
 * a ten jeden przycisk zatrzymuje automat u klienta na pewno.
 */
export type ZasilanieMaszyny = 'RESTART' | 'WYLACZ';

/**
 * BLOKADA WEJSCIA I TRYB KONSERWACJI - na zywo, tunelem otwartej sesji.
 *
 * ⚠️ NIE PRZEZ KOLEJKE ZADAN: czarny ekran ma zaslonic maszyne ZANIM cokolwiek zrobimy,
 * a zadanie dojechaloby po kilkudziesieciu sekundach, czyli PO.
 */
export const sterujPulpitem = (
  machineId: string,
  groupId: string,
  sesja: string,
  stan: { blokada: boolean; konserwacja: boolean },
) =>
  apiPost(
    `/machines/${machineId}/pulpit/${sesja}/sterowanie?groupId=${encodeURIComponent(groupId)}`,
    { groupId, ...stan },
    z.object({ ok: z.boolean(), powod: z.string().nullable(), blokada: z.boolean(), konserwacja: z.boolean() }),
  );

/**
 * CTRL+ALT+DEL — tunelem, ale wykonuje go AGENT, nie pomocnik pulpitu.
 *
 * ⚠️⚠️ ZMIERZONE 16.09.2026: pomocnik jest SYSTEMEM w sesji konsoli i `SendSAS` mu nie przechodzi
 * („obie proby bez skutku, pulpit nadal Default"). Bezpieczną sekwencję uwagi wolno wywołać
 * USŁUDZE z sesji 0 — czyli agentowi. Dlatego to nie jest klawisz wysyłany przez noVNC.
 */
export const wyslijCtrlAltDel = (machineId: string, groupId: string, sesja: string) =>
  apiPost(
    `/machines/${machineId}/pulpit/${sesja}/ctrl-alt-del?groupId=${encodeURIComponent(groupId)}`,
    { groupId },
    z.object({ ok: z.boolean(), powod: z.string().nullable() }),
  );

export const zlecZasilanie = (machineId: string, groupId: string, co: ZasilanieMaszyny) =>
  apiPost(
    `/machines/${machineId}/zasilanie?groupId=${encodeURIComponent(groupId)}`,
    { groupId, co, potwierdzone: true },
    z.object({ taskId: z.string() }),
  );

/**
 * WYRÓWNANIE GEOMETRII AUTOMATU NA CAŁĄ WYSPę.
 *
 * ⚠️ ROZSYŁA TO, CO PORTAL JUŽ WIE, do baz, które tego nie mają — akcją `I`. Węzły, które
 * te wiersze mają, pominą je same (wartownik agenta); brakujące zostaną założone. Rozjazdu
 * „ta sama liczba wierszy, inna zawartość" ta trasa NIE prostuje i nie ma udawać, że prostuje.
 */
/**
 * @param wylacznosc DOMKNIECIE: po wykonaniu rodzenstwo ma w tych tabelach DOKLADNIE te wiersze,
 * a nadmiarowe zostaja usuniete wraz ze stanem magazynowym. Bez tego pola operacja tylko dosypuje.
 */
/**
 * WYRÓWNANIE WSZYSTKICH ROZJECHANYCH AUTOMATÓW WYSPY — jedno kliknięcie zamiast dwunastu.
 *
 * ⚠️ ZGŁOSZONE (01.09.2026, IKEA): „potrzebujemy przycisku, który pociągnie aktualne stany
 * z maszyn na mastera". Źródłem jest WŁAŚCICIEL każdego automatu osobno, nie jedna maszyna.
 */
export const wyrownajWszystkie = (groupId: string, wylacznosc = false) =>
  apiSend(
    'POST',
    '/machines/wyrownaj-wszystkie',
    { groupId, ...(wylacznosc ? { wylacznosc: true } : {}) },
    z.object({
      razem: z.number(),
      ok: z.number(),
      zmian: z.number(),
      automaty: z.array(
        z.object({
          vmid: z.number(),
          nazwa: z.string(),
          ok: z.boolean(),
          zmian: z.number(),
          operacji: z.number(),
          blad: z.string().nullable(),
        }),
      ),
    }),
  );

export const wyrownajGeometrie = (machineId: string, groupId: string, wylacznosc = false) =>
  apiSend(
    'POST',
    `/machines/${machineId}/moduly/wyrownaj`,
    { groupId, ...(wylacznosc ? { wylacznosc: true } : {}) },
    z.object({
      opId: z.string(),
      /**
       * JEDNA OPERACJA NA BEBEN.
       *
       * ⚠️ ZMIERZONE (PESA-2, 29.08.2026): wyrownanie cala maszyna to 3462 wiersze przy granicy
       * 3000 na jedna transakcje, wiec przycisk odmawial. W rozbiciu mieszcza sie wszystkie.
       *
       * ⚠️ `optional`, bo starsza instancja odda samo `opId`.
       */
      operacje: z.array(z.object({ opId: z.string(), picNumber: z.number(), zmian: z.number() })).optional(),
      /** Bebny, ktore nie zmiescily sie nawet osobno — do pokazania, a nie do przemilczenia. */
      pominieteBebny: z.array(z.string()).optional(),
      zmian: z.number(),
      podsumowanie: z.object({
        bebnow: z.number(),
        szuflad: z.number(),
        przegrodek: z.number(),
        kanalow: z.number(),
        drzwiczek: z.number(),
        kolumn: z.number(),
      }),
    }),
  );

/**
 * KARTA AUTOMATU — jedna maszyna, wszystko, co portal o niej wie.
 *
 * ⚠️ POLA `optional`, BO STARSZA INSTANCJA ICH NIE ODDA. Portal i klient wdrażają się razem, ale
 * podczas wdrożenia przez chwilę żyją obie wersje — a karta, która wywala się na walidacji, jest
 * gorsza niż karta z jedną pustą sekcją.
 */
const KartaAutomatuSchema = z.object({
  automat: z.object({
    id: z.string(),
    vmid: z.number(),
    name: z.string(),
    vmCode: z.string().nullable(),
    online: z.boolean(),
    lastSeenAt: z.string().nullable(),
    sourceNodeId: z.string(),
    /** Obecność liczona tak samo, jak na liście automatów — patrz `agent-presence.ts` w API. */
    obecnosc: z.string().optional(),
    kontaktAgenta: z.string().nullable().optional(),
    geometria: z.object({
      bebnow: z.number(),
      bebnowZnanych: z.number(),
      szuflad: z.number(),
      kolumn: z.number(),
      przegrodek: z.number(),
      towarow: z.number(),
    }),
  }),
  wyspa: z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    tryb: z.string(),
    wydaniaRodzenstwa: z.boolean(),
    masterDatabase: z.string().nullable(),
    timeZone: z.string().nullable(),
    zegarNaprawiaj: z.boolean(),
    zegarProgSekund: z.number(),
  }),
  wezel: z
    .object({
      nodeId: z.string(),
      version: z.string().nullable(),
      kind: z.string(),
      nodeRole: z.string().nullable(),
      potwierdzony: z.boolean(),
      enrolledAt: z.string(),
      lastSeenAt: z.string().nullable(),
      hostname: z.string().nullable(),
      machineIp: z.string().nullable(),
      lastSeenIp: z.string().nullable(),
      sps: z.object({ serwer: z.string().nullable(), baza: z.string().nullable(), vmid: z.number().nullable() }),
      dysk: z.object({
        wolneBajty: z.number().nullable(),
        caleBajty: z.number().nullable(),
        wolumen: z.string().nullable(),
        kiedy: z.string().nullable(),
      }),
      zegar: z.object({
        rozjazdSekund: z.number().nullable(),
        uslugaStart: z.string().nullable(),
        uslugaDziala: z.boolean().nullable(),
        strefa: z.string().nullable(),
        offsetMinut: z.number().nullable(),
        czasMaszyny: z.string().nullable(),
      }),
      /** Wersja Windows i silnika SQL — zgłaszane tylko przy zmianie, więc `optional`. */
      system: z
        .object({
          osName: z.string().nullable(),
          osVersion: z.string().nullable(),
          sqlVersion: z.string().nullable(),
          sqlEdition: z.string().nullable(),
          kiedy: z.string().nullable(),
        })
        .optional(),
      problem: z.string().nullable(),
      problemO: z.string().nullable(),
      uwagi: z.array(z.string()).default([]),
      uwagiO: z.string().nullable(),
    })
    .nullable(),
  synchronizacja: z
    .object({
      rodzenstwoWlaczone: z.boolean(),
      kursor: z.number().nullable(),
      szczytObcych: z.number(),
      wpisanych: z.number(),
      pytalO: z.string().nullable(),
      meldunekO: z.string().nullable(),
      blad: z.string().nullable(),
    })
    .nullable(),
  ustawieniaSps: z
    .array(
      z.object({
        param: z.string(),
        value: z.string().nullable(),
        valueCustom: z.string().nullable(),
        descr: z.string().nullable(),
        paramType: z.string().nullable(),
      }),
    )
    .default([]),
  wydania: z.object({
    razem: z.number(),
    ostatnie: z.string().nullable(),
    najczestsze: z
      .array(
        z.object({
          itemNumber: z.string().nullable(),
          name: z.string().nullable(),
          sztuk: z.number(),
          wydan: z.number(),
        }),
      )
      .default([]),
  }),
  ruch: z
    .object({
      dni: z.number(),
      razemDoPortalu: z.number(),
      razemZPortalu: z.number(),
      razemZadan: z.number(),
      rodzaje: z.array(z.object({ kind: z.string(), doPortalu: z.number(), zPortalu: z.number(), zadan: z.number() })),
    })
    .nullable(),
});

export type KartaAutomatu = z.infer<typeof KartaAutomatuSchema>;

export const fetchKartaAutomatu = (id: string, groupId: string) =>
  apiGet(`/machines/${id}/card?groupId=${encodeURIComponent(groupId)}`, KartaAutomatuSchema);

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  DOKŁADNY ROZJAZD Z BAZ — zamówione 09.09.2026: „przycisk, który faktycznie odpyta te bazy
 *  selectem i wypluje dokładny rozjazd".
 *
 *  ⚠️ DWA WYWOŁANIA, BO TO DWIE RÓŻNE RZECZY: `zlec` wkłada zadania do kolejki agentów,
 *  `wynik` pyta o najnowszy odczyt. Odpowiedź przychodzi po jednym–dwóch cyklach agenta, więc
 *  ekran odpytuje `wynik` co kilka sekund, dopóki stan to `CZEKA`.
 * ════════════════════════════════════════════════════════════════════════════════
 */
export const WynikOdczytuRozjazduSchema = z.object({
  stan: z.enum(['BRAK', 'CZEKA', 'GOTOWE']),
  kiedy: z.string().nullable(),
  wezly: z.array(
    z.object({
      nodeId: z.string(),
      stan: z.string(),
      wierszy: z.number().nullable(),
      obcietych: z.number(),
      blad: z.string().nullable(),
    }),
  ),
  brakujace: z.array(
    z.object({
      klucz: z.string(),
      maja: z.array(z.string()),
      nieMaja: z.array(z.string()),
      wartosci: z.record(z.string(), z.string().nullable()),
    }),
  ),
  rozne: z.array(
    z.object({
      klucz: z.string(),
      kolumny: z.array(
        z.object({
          kolumna: z.string(),
          grupy: z.array(z.object({ wartosc: z.string().nullable(), wezly: z.array(z.string()) })),
        }),
      ),
    }),
  ),
  kluczyRazem: z.number(),
  /**
   * ⚠️ Kolumny maszyny objęte tym odczytem. Pusto = zwykłe porównanie konfiguracji; niepusto =
   * ekran ogląda STAN MAGAZYNOWY, czyli inne pytanie i inna interpretacja różnic.
   *
   * ⚠️ `nullish`, bo starsza instancja tego pola nie odda — a schemat ostrzejszy od odpowiedzi
   * psuje udany odczyt.
   */
  kolumnyMaszyny: z.array(z.string()).nullish(),
  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  PORTAL JAKO STRONA PORÓWNANIA — `null` znaczy „portal nie umie odtworzyć klucza tej tabeli",
   *  a NIE „portal nie ma ani jednego wiersza". Ekran musi te dwa stany rozróżnić.
   *
   *  ⚠️ `.optional()`, bo instancja starsza od tej zmiany pola nie odda — a wtedy okno rozjazdu
   *  ma pokazać się bez tej linijki, a nie wywrócić na walidacji.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  portal: z
    .object({
      wierszy: z.number(),
      brakujeWPortalu: z.number(),
      nadmiarWPortalu: z.number(),
      przykladyBrakuje: z.array(z.string()),
      przykladyNadmiar: z.array(z.string()),
      niepewne: z.boolean(),
    })
    .nullable()
    .optional(),
  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   *  CO ZROBI „Doślij brakujące" — policzone przez serwer tą samą regułą, którą potem wykonuje.
   *
   *  ⚠️⚠️ NIE LICZYĆ TEGO PO STRONIE EKRANU. Od 23.09.2026 nie każdy brak jedzie: przy tabeli
   *  jednego automatu pozostałości w kopiach zostają, a przegródki zajęte u każdego adresata innym
   *  towarem nie jadą (wariant B, 24.09.2026). `brakujace.length` na przycisku obiecywałoby więc
   *  więcej, niż akcja zrobi.
   *
   *  ⚠️ `.nullish()`, bo starsza instancja tego pola nie odda — wtedy przycisk zachowuje się
   *  jak dotąd, zamiast wywrócić udany odczyt na walidacji.
   * ══════════════════════════════════════════════════════════════════════════════════════════════
   */
  doslanie: z
    .object({
      mozliwe: z.boolean(),
      powod: z.string().nullable(),
      wierszy: z.number(),
      tylkoWKopiach: z.number(),
      /** Przegródki zajęte u każdego adresata innym towarem — nie jadą (wariant B). Starsza instancja go nie odda. */
      zajetych: z.number().optional(),
    })
    .nullish(),
});

export type WynikOdczytuRozjazdu = z.infer<typeof WynikOdczytuRozjazduSchema>;

/**
 * ⚠️⚠️ `vmid: null` ZNACZY „TABELA WSPÓLNA WYSPY" (kadra: `Users`, członkostwa) — dołożone
 * 14.09.2026 po zgłoszeniu z Donaldsona. Te tabele nie mają kolumny `VMID`, więc numer nie jest
 * tu pominięty przez niedopatrzenie: jego BRAK jest treścią żądania. Portal odmawia obu pomyłek
 * (tabela automatu bez numeru, tabela wyspy z numerem) zdaniem, a nie cichym zerem.
 */
const adresOdczytu = (groupId: string, tabela: string, vmid: number | null) =>
  `/machines/rozjazd/odczyt?groupId=${encodeURIComponent(groupId)}&tabela=${encodeURIComponent(tabela)}` +
  (vmid === null ? '' : `&vmid=${vmid}`);

/** ⚠️ Pola `vmid` przy tabeli wyspowej NIE WYSYŁAMY — schemat żądania jest `strict`, a `null` nie jest liczbą. */
const cialoOdczytu = (groupId: string, tabela: string, vmid: number | null) =>
  vmid === null ? { groupId, tabela } : { groupId, tabela, vmid };

export const zlecOdczytRozjazdu = (groupId: string, tabela: string, vmid: number | null) =>
  apiPost(
    '/machines/rozjazd/odczyt',
    cialoOdczytu(groupId, tabela, vmid),
    z.object({
      batchId: z.string(),
      wezly: z.array(z.string()),
      /** ⚠️ `nullish`: starsza instancja tych pól nie odda, a schemat ostrzejszy od odpowiedzi psuje udany odczyt. */
      zaStare: z.array(z.string()).nullish(),
      wersjaWymagana: z.string().nullish(),
    }),
  );

export const wynikOdczytuRozjazdu = (groupId: string, tabela: string, vmid: number | null) =>
  apiGet(adresOdczytu(groupId, tabela, vmid), WynikOdczytuRozjazduSchema);

/**
 * DOSŁANIE BRAKUJĄCYCH WIERSZY — druga decyzja człowieka, po przejrzeniu odczytu.
 *
 * ⚠️⚠️ ROZSTRZYGNIĘTE Z UŻYTKOWNIKIEM 09.09.2026: rozjazd, który JUŻ jest na produkcji,
 * uzgadniamy „na żądanie, z portalu" — nie sam i nie przy pierwszym cyklu.
 *
 * ⚠️ `powod` NIE JEST BŁĘDEM. Odmowa („nie ma odczytu", „odczyt obcięty limitem", „nic nie
 * brakuje") wraca jako `200` ze zdaniem, bo to normalne stany tego przycisku, a nie awarie.
 * Ekran ma je pokazać jako informację, nie jako czerwony komunikat.
 */
export const dosliBrakujaceWiersze = (groupId: string, tabela: string, vmid: number | null) =>
  apiPost(
    '/machines/rozjazd/dosli',
    cialoOdczytu(groupId, tabela, vmid),
    z.object({
      zakolejkowanych: z.number(),
      opId: z.string().nullable(),
      powod: z.string().nullable(),
      /** Ile braków było tylko w kopiach innych baz — nie pojechały. `optional`: starsza instancja go nie odda. */
      tylkoWKopiach: z.number().optional(),
      /** Ile przegródek pominięto, bo u każdego adresata leży w nich inny towar. */
      zajetych: z.number().optional(),
    }),
  );

/**
 * OTWARCIE SESJI PULPITU NA ŻYWO.
 *
 * ⚠️⚠️ ODPOWIEDŹ NIESIE HASŁO JEDNORAZOWE tej sesji — podajemy je noVNC za człowieka. Jest ważne
 * tylko dla tego jednego połączenia, a serwer RFB na maszynie nasłuchuje wyłącznie na 127.0.0.1,
 * więc jedyną drogą do niego jest tunel agenta.
 */
export const otworzPulpit = (groupId: string, machineId: string) =>
  apiPost(
    `/machines/${machineId}/pulpit?groupId=${encodeURIComponent(groupId)}`,
    {},
    z.object({
      sesja: z.string(),
      haslo: z.string(),
      nodeId: z.string(),
      maszyna: z.string(),
      taskId: z.string(),
    }),
  );

/**
 * DLACZEGO OKNO PULPITU NIE POKAZUJE OBRAZU.
 *
 * ⚠️⚠️ TRZY RÓŻNE AWARIE WYGLĄDAŁY IDENTYCZNIE („Połączenie zerwane") przy pierwszej próbie na
 * testówce: agent nie podłączył się do tunelu, agent zgłosił błąd przy stawianiu serwera, sesja
 * padła z limitu czasu. Powód znał portal, tylko nikt go nie pytał.
 */
export const stanPulpitu = (groupId: string, machineId: string, sesja: string) =>
  apiGet(
    `/machines/${machineId}/pulpit/${sesja}?groupId=${encodeURIComponent(groupId)}`,
    z.object({
      stan: z.enum(['CZEKA', 'POLACZONA', 'ZAMKNIETA', 'NIEZNANA']),
      powod: z.string().nullish(),
      nodeId: z.string().nullish(),
      zadanie: z
        .object({
          status: z.string(),
          error: z.string().nullish(),
          note: z.string().nullish(),
          progress: z.number().nullish(),
        })
        .nullish(),
    }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ZAJĘTOŚĆ AUTOMATÓW I TO, ZA ILE PORTAL RĘCZY.
 *
 *  ⚠️⚠️ POWSTAŁO Z AWARII IKEA (18-21.09.2026): portal pokazywał 62,4 %, automat miał 80,0 %,
 *  master 81,1 %. Sama liczba nie była problemem — problemem było to, że nikt nie miał jak
 *  zobaczyć, że jest nieaktualna. Ekran podawał ją tym samym krojem co liczbę świeżą.
 *
 *  ⚠️ `zamrozonych` to wiersze, na które portal ma niewykonaną zmianę: jego meldunki o ich stanie
 *  są odrzucane, więc zapisane `QTYCURRENT` pochodzi sprzed tej zmiany.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const ZajetoscSchema = z.object({
  automaty: z.array(
    z.object({
      machineId: z.string(),
      vmid: z.number(),
      nazwa: z.string(),
      qtyCurrent: z.number(),
      qtyMax: z.number(),
      procent: z.number().nullable(),
      pozycji: z.number(),
      zamrozonych: z.number(),
      udzialZamrozonych: z.number(),
      stan: z.string(),
      wlasciciel: z.string().nullable(),
      minutOdKontaktuWlasciciela: z.number().nullable(),
      /**
       * ⚠️⚠️ CO O TYM AUTOMACIE MÓWIĄ BAZY WĘZŁÓW. Pusta lista znaczy „nikt jeszcze nie pytał",
       * a NIE „wszyscy się zgadzają" — to właśnie brak odpowiedzi wyglądał przez trzy dni jak zgoda.
       */
      wezly: z.array(
        z.object({
          nodeId: z.string(),
          procent: z.number().nullable(),
          qtyCurrent: z.number(),
          qtyMax: z.number(),
          pozycji: z.number(),
        }),
      ),
    }),
  ),
  /** Kolejka była większa od granicy liczenia — liczba zamrożonych jest wtedy zaniżona. */
  niepelne: z.boolean(),
  odczytKiedy: z.string().nullable(),
  odczytCzeka: z.boolean(),
});

export type Zajetosc = z.infer<typeof ZajetoscSchema>;

export const fetchZajetosc = (groupId: string) =>
  apiGet(`/machines/zajetosc?groupId=${encodeURIComponent(groupId)}`, ZajetoscSchema);

export const zlecOdczytZajetosci = (groupId: string) =>
  apiPost(
    '/machines/zajetosc/odczyt',
    { groupId },
    z.object({ batchId: z.string(), wezly: z.array(z.string()), zaStare: z.array(z.string()) }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ROZJAZD STANU MAGAZYNOWEGO JEDNEGO AUTOMATU — wiersz po wierszu, z portalem jako stroną.
 *
 *  ⚠️⚠️ OSOBNA TRASA OD ODCZYTU ROZJAZDU, bo to INNE PYTANIE. Tamten porównuje konfigurację
 *  i świadomie wycina stan magazynowy; tutaj ta różnica jest całą treścią.
 *
 *  ⚠️ ZMIERZONE, PO CO (21.09.2026): portal 80,8 %, świeża kopia mastera 76,5 %, ta sama liczba
 *  pozycji. Sumy mówiły, że rozjazd JEST, i nic więcej — a bez wierszy nie wiadomo, czy to jedna
 *  przegródka o sto sztuk, czy sto przegródek po jednej.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const zlecOdczytStanu = (groupId: string, vmid: number) =>
  apiPost(
    '/machines/rozjazd/stan-magazynowy',
    { groupId, vmid },
    z.object({
      batchId: z.string(),
      wezly: z.array(z.string()),
      zaStare: z.array(z.string()).nullish(),
      wersjaWymagana: z.string().nullish(),
    }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  WYRÓWNANIE STANU AUTOMATU — rozsyła to, co portal ma u siebie, do POZOSTAŁYCH węzłów.
 *
 *  ⚠️⚠️ WŁAŚCICIELA NIE DOTYKA: u niego `QTYCURRENT` jest prawdą, a nasza wartość pochodzi z jego
 *  wcześniejszego meldunku — wpisanie mu jej z powrotem cofnęłoby wydania zrobione w międzyczasie.
 *
 *  ⚠️ ROZSYŁA STAN PORTALU, A NIE „PRAWDĘ". Jeśli to portal jest do tyłu, rozniesie jego
 *  opóźnienie — dlatego przycisk stoi POD ekranem rozjazdu, na którym widać, kto jest do tyłu.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  CO ZROBIĄ OBA PRZYCISKI STANU — zdanie liczone przez portal, bez żadnego skutku.
 *
 *  ⚠️⚠️ ZDANIE SKŁADA SERWER, A NIE EKRAN. Tylko portal wie, ile pozycji się różni, ile jest
 *  zamrożonych i ilu węzłów dotyczy rozsyłka. Zdanie sklejane w kliencie z kilku liczb rozjechałoby
 *  się z tym, co przycisk naprawdę robi — a to jest najgorszy rodzaj usterki w tym miejscu: uczy
 *  człowieka, że ekranu nie warto czytać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const podgladStanuAutomatu = (groupId: string, vmid: number) =>
  apiGet(
    `/machines/zajetosc/podglad?groupId=${encodeURIComponent(groupId)}&vmid=${vmid}`,
    z.object({
      przyjecie: z.object({
        mozliwe: z.boolean(),
        /** `null` = da się; napis = powód, dla którego nie i co z tym zrobić. */
        powod: z.string().nullable(),
        wlasciciel: z.string().nullable(),
        kiedy: z.string().nullable(),
        pozycji: z.number(),
        opis: z.string(),
        zastrzezenia: z.array(z.string()),
      }),
      rozeslanie: z.object({
        pozycji: z.number(),
        wezlow: z.number(),
        wlasciciel: z.string().nullable(),
        opis: z.string(),
      }),
    }),
  );

/** Krok PIERWSZY z dwóch: portal uczy się stanu od właściciela. Nic nie jedzie na maszyny. */
export const przyjmijStanOdWlasciciela = (groupId: string, vmid: number) =>
  apiPost(
    '/machines/zajetosc/przyjmij',
    { groupId, vmid },
    z.object({ zmienionych: z.number(), zastrzezenia: z.array(z.string()), opis: z.string() }),
  );

export const wyrownajStanAutomatu = (groupId: string, vmid: number) =>
  apiPost(
    '/machines/zajetosc/wyrownaj',
    { groupId, vmid },
    z.object({ opId: z.string().nullable(), pozycji: z.number() }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PRZYJĘCIE STANU OD WSZYSTKICH AUTOMATÓW — odczyt bazy każdego właściciela i przyjęcie po nim.
 *
 *  ⚠️ ZAMÓWIONE 24.09.2026 (IKEA, Vending+). Nic nie jedzie na maszyny; uzasadnienie i granice
 *  w `api/src/machines/przyjecie-wszystkich.ts`. Portal przyjmuje stan sam, zaraz po odczycie —
 *  ekran tylko pokazuje, jak idzie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const przyjmijStanOdWszystkich = (groupId: string) =>
  apiSend(
    'POST',
    '/machines/zajetosc/przyjmij-wszystkie',
    { groupId },
    z.object({
      zlecenieId: z.string(),
      automatow: z.number(),
      bezWlasciciela: z.array(z.number()),
      zaStare: z.array(z.string()),
    }),
  );

const StanPrzyjeciaAutomatuSchema = z.object({
  vmid: z.number(),
  wezel: z.string(),
  nazwa: z.string().nullable(),
  stan: z.enum(['CZEKA', 'BLAD', 'PRZYJETO', 'NIE']),
  zmienionych: z.number().optional(),
  zamrozonych: z.number().optional(),
  nieznanych: z.number().optional(),
  powod: z.string().optional(),
});

export type StanPrzyjeciaAutomatu = z.infer<typeof StanPrzyjeciaAutomatuSchema>;

export const stanPrzyjeciaWszystkich = (groupId: string, zlecenieId: string) =>
  apiGet(
    `/machines/zajetosc/przyjmij-wszystkie?groupId=${encodeURIComponent(groupId)}&zlecenieId=${encodeURIComponent(zlecenieId)}`,
    z.object({ automaty: z.array(StanPrzyjeciaAutomatuSchema), zakonczone: z.boolean() }),
  );
